<template>
  <div class="view-container">
    <div class="stats-row">
      <div class="stat-card" v-for="s in stats" :key="s.label" :style="{ borderTopColor: s.color }">
        <div class="stat-icon" :style="{ background: s.color }"><i :class="s.icon"></i></div>
        <div class="stat-info">
          <div class="stat-value">{{ s.value }}</div>
          <div class="stat-label">{{ s.label }}</div>
        </div>
      </div>
    </div>

    <div class="content-grid">
      <div class="grid-left">
        <div class="card">
          <div class="card-header"><h3><i class="fas fa-clock"></i> 最近会话</h3></div>
          <div class="card-body no-padding">
            <div v-for="conv in recentConvs" :key="conv.id" class="conv-item" @click="openConv(conv)">
              <div class="avatar" :style="{ background: conv.avatarColor }">{{ conv.avatar }}</div>
              <div class="conv-info">
                <div class="conv-top">
                  <span class="conv-name">{{ conv.name }}</span>
                  <span class="conv-time">{{ conv.time }}</span>
                </div>
                <div class="conv-bottom">
                  <span class="conv-last">{{ conv.lastMsg }}</span>
                  <span v-if="conv.unread > 0" class="unread-badge">{{ conv.unread }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="grid-right">
        <div class="card">
          <div class="card-header"><h3><i class="fas fa-chart-bar"></i> 消息统计</h3></div>
          <div class="card-body">
            <VChart class="chart" :option="msgChart" autoresize />
          </div>
        </div>
        <div class="card">
          <div class="card-header"><h3><i class="fas fa-users"></i> 在线联系人</h3></div>
          <div class="card-body">
            <div class="online-list">
              <div v-for="u in onlineUsers" :key="u.id" class="online-user">
                <div class="online-avatar" :style="{ background: u.color }">{{ u.avatar }}</div>
                <span class="online-name">{{ u.name }}</span>
                <span class="online-dot"></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart, LineChart } from 'echarts/charts'
import { TitleComponent, TooltipComponent, LegendComponent, GridComponent } from 'echarts/components'
import VChart from 'vue-echarts'
import { getMessageStats, getRecentConversations } from '../api/message'

use([CanvasRenderer, BarChart, LineChart, TitleComponent, TooltipComponent, LegendComponent, GridComponent])

const loading = ref(false)

const stats = ref([
  { label: '未读消息', value: 0, icon: 'fas fa-envelope', color: '#06b6d4' },
  { label: '今日消息', value: 0, icon: 'fas fa-comment-dots', color: '#10b981' },
  { label: '在线联系人', value: 0, icon: 'fas fa-user-check', color: '#f59e0b' },
  { label: '我的会话', value: 0, icon: 'fas fa-comments', color: '#8b5cf6' }
])

const recentConvs = ref([])

const onlineUsers = ref([
  { id: 1, name: '张三', avatar: '张', color: '#3b82f6' },
  { id: 2, name: '李四', avatar: '李', color: '#10b981' },
  { id: 3, name: '王五', avatar: '王', color: '#f59e0b' },
  { id: 4, name: '赵六', avatar: '赵', color: '#8b5cf6' },
  { id: 5, name: '孙七', avatar: '孙', color: '#ef4444' },
  { id: 6, name: '周八', avatar: '周', color: '#ec4899' }
])

const msgChart = ref({
  tooltip: { trigger: 'axis' },
  grid: { left: 45, right: 20, top: 20, bottom: 30 },
  xAxis: { type: 'category', data: ['周一', '周二', '周三', '周四', '周五', '周六', '周日'], axisLabel: { fontSize: 10 } },
  yAxis: { type: 'value', axisLabel: { fontSize: 10 } },
  series: [
    { name: '发送', type: 'bar', data: [120, 145, 162, 138, 186, 45, 28], itemStyle: { color: '#06b6d4', borderRadius: [3, 3, 0, 0] } },
    { name: '接收', type: 'bar', data: [98, 132, 155, 126, 172, 52, 35], itemStyle: { color: '#8b5cf6', borderRadius: [3, 3, 0, 0] } },
    { name: '未读', type: 'line', data: [15, 22, 18, 28, 32, 8, 5], smooth: true, itemStyle: { color: '#ef4444' } }
  ]
})

/**
 * 加载消息统计数据
 */
const loadMessageStats = async () => {
  try {
    loading.value = true
    const response = await getMessageStats()
    
    if (response.data) {
      const { unreadCount, todayCount, totalCount, privateCount } = response.data
      
      stats.value[0].value = unreadCount || 0
      stats.value[1].value = todayCount || 0
      stats.value[2].value = onlineUsers.value.length // 在线联系人暂时使用固定数据
      stats.value[3].value = privateCount || 0
    }
  } catch (error) {
    console.error('加载消息统计失败:', error)
  } finally {
    loading.value = false
  }
}

/**
 * 加载最近会话
 */
const loadRecentConversations = async () => {
  try {
    const response = await getRecentConversations(6)
    
    if (response.data) {
      recentConvs.value = response.data.map(conv => ({
        id: conv.sendAccount,
        name: conv.sendName || conv.sendAccount,
        avatar: getAvatar(conv.sendName || conv.sendAccount),
        avatarColor: getRandomColor(),
        lastMsg: getPreview(conv.lastMessage),
        time: formatTime(conv.lastTime),
        unread: conv.unreadCount || 0
      }))
    }
  } catch (error) {
    console.error('加载最近会话失败:', error)
  }
}

/**
 * 工具函数
 */
const getAvatar = (name) => {
  if (!name) return '?'
  return name.substring(0, 1)
}

const getRandomColor = () => {
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#ec4899', '#06b6d4']
  return colors[Math.floor(Math.random() * colors.length)]
}

const getPreview = (content) => {
  if (!content) return ''
  const text = content.replace(/<[^>]*>/g, '')
  return text.length > 30 ? text.substring(0, 30) + '...' : text
}

const formatTime = (datetime) => {
  if (!datetime) return ''
  
  const d = new Date(datetime)
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  
  if (minutes < 1) return '刚刚'
  if (minutes < 60) return `${minutes}分钟前`
  if (hours < 24) return `${hours}小时前`
  if (days === 1) return '昨天'
  if (days < 7) return `${days}天前`
  
  return d.toLocaleDateString('zh-CN')
}

const openConv = (conv) => {
  // TODO: 跳转到聊天界面
  console.log('打开会话:', conv.name)
}

onMounted(() => {
  loadMessageStats()
  loadRecentConversations()
})
</script>

<style scoped>
.view-container { padding: 20px; background: #f5f7fa; overflow-y: auto; max-height: calc(100vh - 130px); }
.stats-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 20px; }
.stat-card { background: #fff; border-radius: 8px; border: 1px solid #e8ecf1; border-top: 3px solid #06b6d4; padding: 20px; display: flex; align-items: center; gap: 16px; }
.stat-icon { width: 48px; height: 48px; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 20px; }
.stat-value { font-size: 28px; font-weight: 700; color: #1f2937; }
.stat-label { font-size: 13px; color: #6b7280; margin-top: 2px; }
.content-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.grid-left, .grid-right { display: flex; flex-direction: column; gap: 20px; }
.card { background: #fff; border-radius: 8px; border: 1px solid #e8ecf1; overflow: hidden; }
.card-header { padding: 12px 16px; border-bottom: 1px solid #e8ecf1; background: #fafbfc; }
.card-header h3 { margin: 0; font-size: 14px; font-weight: 600; color: #374151; display: flex; align-items: center; gap: 8px; }
.card-body { padding: 12px; }
.card-body.no-padding { padding: 0; }

.conv-item { display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-bottom: 1px solid #f3f4f6; cursor: pointer; transition: background 0.15s; }
.conv-item:hover { background: #f8fafc; }
.avatar { width: 40px; height: 40px; border-radius: 6px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 14px; font-weight: 600; flex-shrink: 0; }
.conv-info { flex: 1; min-width: 0; }
.conv-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
.conv-name { font-size: 13px; font-weight: 600; color: #1f2937; }
.conv-time { font-size: 11px; color: #9ca3af; }
.conv-bottom { display: flex; justify-content: space-between; align-items: center; }
.conv-last { font-size: 12px; color: #9ca3af; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.unread-badge { background: #ef4444; color: #fff; font-size: 10px; padding: 2px 6px; border-radius: 10px; min-width: 18px; text-align: center; }

.online-list { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
.online-user { display: flex; align-items: center; gap: 8px; padding: 8px; border-radius: 6px; background: #f9fafb; }
.online-avatar { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 12px; font-weight: 600; flex-shrink: 0; }
.online-name { font-size: 12px; color: #374151; flex: 1; }
.online-dot { width: 8px; height: 8px; border-radius: 50%; background: #10b981; flex-shrink: 0; }

.chart { height: 260px; }
</style>
